import {Button, FormControl, Heading, Text, type HeadingProps} from '@primer/react-brand/esm'
import type React from 'react'
import {Fragment, use, useCallback, useRef} from 'react'
import * as z from 'zod'

import {FormContext} from './FormContext'
import {Flash} from '../../Flash/Flash'
import {ConsentExperience} from './components/ConsentExperience/ConsentExperience'
import {Octocaptcha} from './components/Octocaptcha/Octocaptcha'
import {getAnalyticsEvent} from '../../../lib/utils/analytics'
import {t} from '../../../lib/utils/localization'

import styles from './Form.module.css'
import {CountrySelectField} from './components/ConsentExperience/CountrySelect/CountrySelectField'

export type FormProps = React.FormHTMLAttributes<HTMLFormElement> & {children?: React.ReactNode}

const Root = (props: FormProps) => {
  const {onSubmit, children, ...rest} = props

  return (
    <form noValidate onSubmit={onSubmit} {...rest}>
      {children}
    </form>
  )
}

export type FormHeadingProps = Pick<HeadingProps, 'children' | 'size' | 'as'>

const FormHeading = ({children, ...props}: FormHeadingProps) => {
  return (
    <Heading as="h4" {...props}>
      {children}
    </Heading>
  )
}

export type FormSubmitProps = {
  children?: React.ReactNode
} & React.ButtonHTMLAttributes<HTMLButtonElement>

const FormSubmit = (props: FormSubmitProps) => {
  const {children, ...rest} = props

  return (
    <Button variant="primary" type="submit" {...rest}>
      {props.children}
    </Button>
  )
}

const FormErrors = () => {
  const formContext = use(FormContext)

  return (
    <Flash hidden={formContext === undefined || Object.keys(formContext.errors).length === 0} variant="error">
      <Text as="p" weight="semibold" size="200">
        {t('The following fields have errors:')}
      </Text>

      <Text as="p" size="200">
        {Object.keys(formContext?.errors ?? []).map((name, index, collection) => {
          const maybeFormField = formContext?.formFields[name]

          if (maybeFormField === undefined) {
            /**
             * It is very unlikely that we reach this point, but if we do, we silently
             * skips this error.
             */
            return null
          }

          const isLastElement = index === collection.length - 1
          const label = maybeFormField.label ?? maybeFormField.name

          return (
            <Fragment key={maybeFormField.name}>
              <a
                href={`#${maybeFormField.id}`}
                className={styles.FormErrorsItem}
                {...getAnalyticsEvent({
                  action: label,
                  tag: 'hyperlink',
                  context: 'form_errors',
                  location: 'form',
                })}
              >
                {label}
              </a>

              {!isLastElement && ', '}
            </Fragment>
          )
        })}
      </Text>
    </Flash>
  )
}

const FormOctocaptcha = () => {
  const OCTOCAPTCHA_NAME = 'octocaptcha-token'

  const ref = useRef<HTMLInputElement>(null)

  const formContext = use(FormContext)

  const registerProps = formContext?.register(OCTOCAPTCHA_NAME, {
    label: 'CAPTCHA',
    required: true,
    validations: [{message: t('Please complete the CAPTCHA.'), schema: z.string().min(1)}],
  })
  const registeredInputRef = registerProps?.ref
  const inputRef = useCallback(
    (element: HTMLInputElement | null) => {
      ref.current = element
      registeredInputRef?.(element)
    },
    [registeredInputRef],
  )

  const error = formContext?.errors[OCTOCAPTCHA_NAME]
  const validationId = registerProps?.id ? `${registerProps.id}-validation` : undefined

  return (
    <FormControl id={registerProps?.id} validationStatus={typeof error === 'string' ? 'error' : undefined}>
      <Octocaptcha
        id={registerProps?.id}
        aria-describedby={typeof error === 'string' ? validationId : undefined}
        aria-invalid={typeof error === 'string'}
        onComplete={token => {
          if (ref.current !== null) {
            /**
             * React events are not strictly bound to the DOM, so we need to call the value setter
             * of the input element directly to set the value of the octocaptcha token and then
             * dispatch an input event to simulate the user filling the input.
             */
            const valueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set

            valueSetter?.call(ref.current, token.token)

            ref.current.dispatchEvent(new Event('input', {bubbles: true}))
          }
        }}
      />

      <input name={registerProps?.name} onChange={registerProps?.onChange} ref={inputRef} style={{display: 'none'}} />

      {typeof error === 'string' ? <FormControl.Validation>{error}</FormControl.Validation> : null}
    </FormControl>
  )
}

const FormConsentExperience = (props: React.ComponentProps<typeof ConsentExperience>) => {
  return <ConsentExperience {...props} />
}

const Form = Object.assign(Root, {
  Errors: FormErrors,
  Heading: FormHeading,
  Octocaptcha: FormOctocaptcha,
  ConsentExperience: FormConsentExperience,
  Submit: FormSubmit,
  CountrySelectField,
})

export {Form}
